import { CircularProgress, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import useJobPolling from "hooks/useJobPolling";
import RestoreIcon from "@mui/icons-material/Restore";

import { appPath, defectsPath } from "helpers/routePaths";
import { getLocalStorageData } from "helpers/utils";
import { changeDateForSapError } from "helpers/date";
import { HistoryCaptions, NoReadOnly } from "helpers/constants";
import ColourConstants from "helpers/colourConstants";
import { READONLY_ACCESS } from "constants/AccessTypes/AccessTypes";

import {
  getDefectDetail,
  raiseNotification,
} from "services/defects/details";
import { getIntegrationDefinitions, getIntegrationInstances } from "services/integrations";
import { defectsPage } from "services/History/models";

import {
  setHistoryDrawerState,
  showError,
} from "../../../redux/common/actions";

import ActionButtonStyle from "styles/application/ActionButtonStyle";
import { makeStyles } from "tss-react/mui";

import NavDetails from "components/Elements/NavDetails";
import TabTitle from "components/Elements/TabTitle";
import AccessWrapper from "components/Modules/AccessWrapper";
import HistoryBar from "components/Modules/HistorySidebar/HistoryBar";

import Notes from "./Notes";
import DefectImages from "./Images";
import ChangeStatusPopup from "./ChangeStatusPopup";
import Details from "./details";
import Audio from "./Audio";
import Parts from "./Parts";
import IntegrationName from "./IntegrationName";

const AT = ActionButtonStyle();
const media = "@media (max-width: 414px)";

/**
 * Styles
 */
const useStyles = makeStyles()((theme) => ({
  restore: {
    border: "2px solid",
    borderRadius: "100%",
    height: "35px",
    width: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#307ad6",
  },
  buttons: {
    display: "flex",
    marginLeft: "auto",
    [media]: {
      marginLeft: "0px",
      flexDirection: "column",
      marginBottom: "10px",
      gap: "10px",
    },
  },
  raiseDefectButton: {
    display: "flex",
    marginLeft: "auto",
    backgroundColor: "yellow",
    [media]: {
      marginLeft: "0px",
      flexDirection: "column",
      marginBottom: "10px",
      gap: "10px",
    },
  },
  wrapper: {
    display: "flex",
    [media]: {
      marginTop: "10px",
      justifyContent: "space-between",
      flexDirection: "column",
    },
  },
}));

/**
 * SX (styled) objects for inline usage with MUI `sx` prop
 */
const changeStatusButtonSx = {
  "&.MuiButton-root": {
    backgroundColor: "#ED8738",
  },
};

const raiseNotificationButtonSx = {
  textTransform: "uppercase",
  "&.MuiButton-root": {
    backgroundColor: "#23BB79",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "#d5d5d5",
    color: "grey",
  },
};

function DefectsDetails() {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { isHistoryDrawerOpen } = useSelector((state) => state.commonData);
  const { id } = useParams();

  const { customCaptions, siteAppID, application, position } =
    getLocalStorageData("me");

  const [isChangeStatusOpen, setIsChangeStatusOpen] = useState(false);
  const [integrationInstance, setIntegrationInstance] = useState(null);
  const [integrationDefinitions, setIntegrationDefinitions] = useState(null);
  const [defectDetails, setDefectDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRaiseDefectLoading, setIsRaiseDefectLoading] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);

  const readOnly = position?.defectAccess === READONLY_ACCESS;

  // Poll for job status when a job is queued
  const { jobStatus, isPolling, error: pollingError } = useJobPolling(currentJobId);

  const handleToggleChangeStatus = () =>
    setIsChangeStatusOpen((prev) => !prev);

  /**
   * Fetch defect details and integration instance
   */
  const handleFetchDefect = useCallback(async () => {
    try {
      const [defectResponse, integrationResponse, definitionsResponse] = await Promise.all([
        getDefectDetail(id),
        getIntegrationInstances(),
        getIntegrationDefinitions(),
      ]);

      if (defectResponse.status) {
        setDefectDetails(defectResponse.data);
      }

      if (definitionsResponse.status) {
        setIntegrationDefinitions(definitionsResponse.data);
      }

      // Handle integration instances - filter active defect integrations
      if (integrationResponse?.status && definitionsResponse?.status) {
        // Get all defect integration types
        const defectIntegrationTypes = definitionsResponse.data
          ?.filter(def => def.isDefectIntegration)
          .map(def => def.integrationType);

        // Filter for active defect integrations
        const activeDefectInstances = integrationResponse.data?.filter(
          instance => instance.isActive && 
          defectIntegrationTypes.includes(instance.integrationType)
        ) || [];

        // Only set instance if we have active defect integrations
        if (activeDefectInstances.length > 0) {
          const rawInstance = activeDefectInstances[0];
          const formattedInstance = {
            ...rawInstance,
            fieldMappings: rawInstance?.fieldMappings
              ? JSON.parse(rawInstance?.fieldMappings) ?? {}
              : {},
            settings: rawInstance?.settings
              ? JSON.parse(rawInstance?.settings) ?? {}
              : {}
          };
          setIntegrationInstance(formattedInstance);
        } else {
          setIntegrationInstance(null);
        }
      }
    } catch (error) {
      console.error("Error fetching defect details:", error);
      setIntegrationInstance(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    handleFetchDefect();
  }, [handleFetchDefect]);

  // Handle job polling status changes
  useEffect(() => {
    // Update loading state based on polling status
    setIsRaiseDefectLoading(isPolling);

    // Handle job completion
    if (jobStatus && !isPolling && jobStatus.completedAt) {
      // Check if job succeeded or failed based on the message
      const message = jobStatus.message || "";
      const isFailed = message.toLowerCase().includes("sync failed") ||
                      message.toLowerCase().includes("error");

      if (isFailed) {
        // Job failed - show error and re-enable button
        // Remove escaped quotes from the message if present
        let cleanMessage = message;
        try {
          // Try to parse as JSON in case it's double-encoded
          cleanMessage = JSON.parse(message);
        } catch {
          // If not JSON, just remove surrounding quotes
          cleanMessage = message.replace(/^["']|["']$/g, '');
        }
        dispatch(showError(cleanMessage || "Defect sync failed"));
        setCurrentJobId(null);
      } else {
        // Job succeeded - just update button state (re-enable)
        setCurrentJobId(null);
        handleFetchDefect();
      }
    }

    // Handle polling error (including timeout)
    if (pollingError && !isPolling) {
      dispatch(showError(pollingError));
      setCurrentJobId(null);
    }
  }, [jobStatus, isPolling, pollingError, dispatch]);

  const defectStatus = {
    exported: defectDetails?.exported,
    sapError: defectDetails?.sapError,
    sapExportDateTime: defectDetails?.sapExportDateTime,
  };

  const isSynchronized =
    !defectDetails?.sapError && defectDetails?.sapExportDateTime;
  const hasError = !!defectDetails?.sapError;

  // Memoize the canRaiseNotification check to avoid repeated calculations
  const canShowRaiseNotification = useMemo(() => {
    if (!integrationInstance?.id || !integrationDefinitions) return false;

    // Find the definition matching the instance's type
    const definition = integrationDefinitions.find(
      def => def.integrationType === integrationInstance.integrationType
    );
    
    if (!definition?.isDefectIntegration) return false;

    // Check if defect's risk rating ID matches integration settings
    const defectRiskRatingKeys = integrationInstance?.fieldMappings?.EnumValue?.DefectRiskRating;

    // If DefectRiskRating mapping exists, check if current defect's risk rating is in the mapping
    if (defectRiskRatingKeys && typeof defectRiskRatingKeys === 'object' && Object.keys(defectRiskRatingKeys).length > 0) {
      const defectRiskRatingId = defectDetails?.defectRiskRatingID;
      // Only show button if the defect's risk rating ID is in the mapping
      return defectRiskRatingKeys.hasOwnProperty(String(defectRiskRatingId));
    }

    // If no DefectRiskRating mapping exists or is empty, show button by default
    return true;
  }, [integrationInstance, integrationDefinitions, defectDetails?.defectRiskRatingID]);

  const handleRaiseDefect = async () => {
    setIsRaiseDefectLoading(true);
    try {
      const response = await raiseNotification(id);

      // Check for jobId first (async job response)
      if (response.data?.jobId) {
        // Background job was queued - start polling
        setCurrentJobId(response.data.jobId);
        // Note: isRaiseDefectLoading will be managed by the polling useEffect
      } else if (!response.status) {
        // Handle known error (e.g., duplicate notification)
        // Ensure we only set string error messages, not objects
        const errorMessage = typeof response.data === 'string'
          ? response.data
          : response.data?.message || "Failed to raise notification";

        setDefectDetails((prev) => ({
          ...prev,
          sapError: errorMessage,
          sapExportDateTime: null,
        }));
        dispatch(showError(errorMessage));
        setIsRaiseDefectLoading(false);
      } else {
        // Legacy synchronous success: refetch the latest data from the server
        await handleFetchDefect();
        setIsRaiseDefectLoading(false);
      }
    } catch (error) {
      // Generic server error
      await handleFetchDefect();
      dispatch(showError(error?.data ?? "Failed to raise notification."));
      setIsRaiseDefectLoading(false);
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <div className="container">
      <TabTitle
        title={`${customCaptions.defect} ${defectDetails?.number} | ${application.name}`}
      />
      <ChangeStatusPopup
        open={isChangeStatusOpen}
        onClose={handleToggleChangeStatus}
        setDetails={setDefectDetails}
      />

      <HistoryBar
        id={id}
        showhistorybar={isHistoryDrawerOpen}
        dispatch={dispatch}
        fetchdata={(id, pageNumber, pageSize) =>
          defectsPage(id, pageNumber, pageSize)
        }
        origin={HistoryCaptions.defects}
      />

      <div className="topContainerCustomCaptions">
        <NavDetails
          status
          lastSaved={null}
          staticCrumbs={[
            {
              id: 1,
              name: customCaptions?.defectPlural || "Defects",
              url: `${appPath}${defectsPath}`,
            },
            {
              id: 2,
              name: defectDetails.number,
            },
          ]}
          hideLastLogin
          hideVersion
          showCreatedByAt
          showSDefects
          state={{
            modelStatusName: defectDetails?.defectStatusName,
            statusColor:
              defectDetails.defectStatusType === "C"
                ? ColourConstants.green
                : ColourConstants.red,
          }}
          defectDetails={{ defectDetail: defectStatus, defectIntegrationInstance: integrationInstance }}
          time={defectDetails.createdDateTime}
          userName={defectDetails.createdUserName}
        />

        <div className={classes.wrapper}>
          {/* Raise Notification Button */}
          <AccessWrapper access={position?.defectAccess} accessList={NoReadOnly}>
            <div className={classes.buttons}>
              {canShowRaiseNotification && (
                <LoadingButton
                  sx={{
                    ...raiseNotificationButtonSx,
                    height: "37px !important",
                    minHeight: "37px",
                    maxHeight: "37px",
                    fontSize: "13.5px",
                    fontWeight: "bold",
                    fontFamily: "Roboto Condensed",
                    marginRight: "10px",
                    padding: "6px 16px",
                    whiteSpace: "nowrap",
                    "& .MuiLoadingButton-loadingIndicator": {
                      position: "relative",
                      left: 0,
                      marginRight: "8px",
                    },
                  }}
                  onClick={handleRaiseDefect}
                  className={classes.raiseDefectButton}
                  disabled={isSynchronized}
                  loading={isRaiseDefectLoading}
                  loadingPosition="start"
                  loadingIndicator={
                    <CircularProgress size={16} sx={{ color: "#666" }} />
                  }
                  variant="contained"
                >
                  Raise Notification
                </LoadingButton>
              )}
            </div>
          </AccessWrapper>

          {/* Change Status Button */}
          <AccessWrapper access={position?.defectAccess} accessList={NoReadOnly}>
            <div className={classes.buttons}>
              <AT.GeneralButton
                sx={changeStatusButtonSx}
                onClick={handleToggleChangeStatus}
              >
                Change Status
              </AT.GeneralButton>
            </div>
          </AccessWrapper>

          {/* History Drawer Icon */}
          <div
            className="restore"
            onClick={() => dispatch(setHistoryDrawerState(true))}
          >
            <RestoreIcon className={classes.restore} />
          </div>
        </div>
      </div>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Details
            details={defectDetails}
            siteAppID={siteAppID}
            captions={customCaptions}
            defectId={id}
            fetchDefect={handleFetchDefect}
            isReadOnly={readOnly}
            defectDetail={defectStatus}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <Grid item xs={12}>
            <DefectImages
              defectId={id}
              captions={customCaptions}
              isReadOnly={readOnly}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "16px" }}>
            <Notes defectId={id} isReadOnly={readOnly} />
          </Grid>
        </Grid>

        <Grid item xs={12} lg={6}>
          {defectDetails?.audioURL && (
            <Grid item xs={12} style={{ marginBottom: "16px" }}>
              <Audio
                src={defectDetails?.audioURL}
                defectId={id}
                isReadOnly={readOnly}
                setDetails={setDefectDetails}
              />
            </Grid>
          )}

          {application?.showDefectParts && (
            <Grid item xs={12}>
              <Parts
                captions={customCaptions}
                defectId={id}
                isReadOnly={readOnly}
              />
            </Grid>
          )}

          {hasError && (
            <Grid item xs={12} id="integration-name-status">
              <IntegrationName
                integrationName={`${integrationInstance?.name}`}
                lastAttempt={changeDateForSapError(
                  integrationInstance?.lastSyncAttempt
                )}
                sapError={defectStatus?.sapError}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export default DefectsDetails;
